import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useRouter } from 'next/router';

import Btn from '@components/Btn';
import LanguageSelector from '@components/LanguageSelector';
import LoadingPage from '@components/LoadingPage';
import logUserIn from '@actions/logUserIn';
import OneTimePassCode from '@components/RegistrationForm/OneTimePassCode/Index';

import { useI18n } from '@lib/i18n';
import {
	BASE_URL,
	HOME_TITLE,
	HOME_DESCRIPTION,
	AUTH_LOGIN_PLACEHOLDER_EMAIL,
	AUTH_LOGIN_FAILED_LOGIN,
} from '@lib/constants';

import style from './index.module.scss';
import welcomeStyle from '@styles/welcome.module.scss';
import HomepageLogo from '../HomepageLogo';

export default function MembersOnlyHomepage({
	animationClass = '',
	animationData = {},
}) {
	const { t, locale } = useI18n();
	const dispatch = useDispatch();
	const store = useStore();
	const router = useRouter();

	const { authenticated, prospectusId } = useSelector(
		(state) => state.user
	);

	const formRef = useRef(null);
	const requestInProgress = useRef(false);
	const codeCooldownEnd = useRef(0);

	const [showLogin, setShowLogin] = useState(false);
	const [showPasscode, setShowPasscode] = useState(false);
	const [email, setEmail] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [notice, setNotice] = useState('');
	const [passcodeVersion, setPasscodeVersion] = useState(0);
	const [cooldownUntil, setCooldownUntil] = useState(0);

	const codeActionsDisabled = isLoading || cooldownUntil > 0;

	const formattedDescription = HOME_DESCRIPTION.replace(/\\n/g, '\n');

	useEffect(() => {
		if (!cooldownUntil) {
			return;
		}

		const timer = setTimeout(() => {
			setCooldownUntil(0);
		}, Math.max(0, cooldownUntil - Date.now()));

		return () => clearTimeout(timer);
	}, [cooldownUntil]);

	const requestCode = async (resend = false) => {
		if (
			requestInProgress.current ||
			Date.now() < codeCooldownEnd.current
		) {
			return;
		}

		const loginEmail = email.trim();

		if (!loginEmail) {
			setError(
				t('auth.email_required', 'Please enter your email address.')
			);
			return;
		}

		requestInProgress.current = true;
		setIsLoading(true);
		setError('');
		setNotice('');

		try {
			const response = await fetch(
				`${BASE_URL}/wp-json/authenicate/v1/requestCode`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						email: loginEmail,
						locale: locale || 'en',
					}),
				}
			);

			if (!response.ok) {
				setError(
					t(
						'auth.code_request_failed',
						'We could not send a login code. Please try again.'
					)
				);
				return;
			}

			const cooldownEnd = Date.now() + 15000;

			codeCooldownEnd.current = cooldownEnd;
			setCooldownUntil(cooldownEnd);

			setEmail(loginEmail);
			setShowPasscode(true);
			setPasscodeVersion((version) => version + 1);

			if (resend) {
				setNotice(
					t(
						'auth.new_code_sent',
						'A new code has been sent to your email.'
					)
				);
			}
		} catch {
			setError(
				t(
					'auth.connection_error',
					'Unable to connect. Please try again.'
				)
			);
		} finally {
			requestInProgress.current = false;
			setIsLoading(false);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (requestInProgress.current) {
			return;
		}

		if (!showPasscode) {
			await requestCode();
			return;
		}

		const digits = Array.from({ length: 6 }, (_, index) => {
			const input = formRef.current?.querySelector(
				`[id="code${index + 1}"]`
			);

			return input?.value.trim() || '';
		});

		if (!digits.every((digit) => /^\d$/.test(digit))) {
			setNotice('');
			setError(
				t(
					'auth.code_required',
					'Please enter the full six-digit code.'
				)
			);
			return;
		}

		requestInProgress.current = true;
		setIsLoading(true);
		setError('');
		setNotice('');

		let redirected = false;

		try {
			const loginOk = await dispatch(
				logUserIn({
					email: email.trim(),
					password: digits.join(''),
					awaitProspectus: true,
				})
			);

			if (!loginOk) {
				setError(
					t(
						'auth.login_failed',
						AUTH_LOGIN_FAILED_LOGIN ||
							'Unable to log in. Please check your code and try again.'
					)
				);
				return;
			}

			// Read the latest state after the login action has finished.
			const loggedInUser = store.getState().user;

			redirected = await router.replace(
				loggedInUser.prospectusId
					? `/prospectus/${encodeURIComponent(
							String(loggedInUser.prospectusId)
						)}`
					: '/personalise'
			);

			if (!redirected) {
				setError(
					t(
						'auth.connection_error',
						'Unable to connect. Please try again.'
					)
				);
			}
		} catch {
			setError(
				t(
					'auth.connection_error',
					'Unable to connect. Please try again.'
				)
			);
		} finally {
			if (!redirected) {
				requestInProgress.current = false;
				setIsLoading(false);
			}
		}
	};

	const handleChangeEmail = () => {
		if (
			requestInProgress.current ||
			Date.now() < codeCooldownEnd.current
		) {
			return;
		}

		setShowPasscode(false);
		setError('');
		setNotice('');
	};

	const handleProspectusClick = () => {
		router.push(
			prospectusId
				? `/prospectus/${encodeURIComponent(String(prospectusId))}`
				: '/personalise'
		);
	};

	if (isLoading && showPasscode) {
		return <LoadingPage />;
	}

	return (
		<>
			<HomepageLogo
				animationClass={animationClass}
				animationData={animationData}
			/>

			{(!showLogin || (authenticated && !error)) && (
				<>
					<h1
						className={`has-text-align-center has-2-font-size ${animationClass}`}
						{...animationData}
					>
						{t('home.title', HOME_TITLE)}
					</h1>

					<p
						style={{ whiteSpace: 'pre-wrap' }}
						className={`has-text-align-center mb-5 ${animationClass}`}
						{...animationData}
					>
						{t('home.description', formattedDescription)}
					</p>
				</>
			)}

			{authenticated && !error ? (
				<div
					className={`is-flex is-justify-content-center is-flex-wrap-wrap has-text-align-center mb-5 homeLayout__buttons ${welcomeStyle.btnContainer}`}
				>
					<Btn
						type="button"
						customClass={['primary', 'buttonTextColours']}
						onClick={handleProspectusClick}
					>
						{prospectusId
							? t('home.view_handbook', 'View handbook')
							: t('home.createProspectus', 'Create yours now')}
					</Btn>

					<LanguageSelector />
				</div>
			) : !showLogin ? (
				<>
					<div
						className={`is-flex is-justify-content-center is-flex-wrap-wrap has-text-align-center mb-5 homeLayout__buttons ${welcomeStyle.btnContainer}`}
					>
						<Btn
							type="button"
							customClass={['primary', 'buttonTextColours']}
							onClick={() => setShowLogin(true)}
						>
							{t('home.view_prospectus', 'View Prospectus')}
						</Btn>
					</div>

					<LanguageSelector />
				</>
			) : (
				<div className={style.login}>
					<h1 className="has-text-align-center has-2-font-size mt-2 mb-2">
						{showPasscode
							? t('auth.members_code_title', 'Check your email')
							: t(
									'members_only.members_login_title',
									'Log in to view and access your handbook'
								)}
					</h1>

					<p className="has-text-align-center mt-0 mb-4">
						{showPasscode ? (
							<>
								{t(
									'auth.members_code_description',
									'Enter the six-digit code we sent to'
								)}{' '}
								<span className={style.changeEmail}>
									{codeActionsDisabled ? (
										<strong>{email}</strong>
									) : (
										<a
											href="#homepageEmailAddress"
											className="has-text-weight-bold is-underlined"
											aria-label={`${t(
												'auth.change_email_address',
												'Change email address'
											)}: ${email}`}
											onClick={(event) => {
												event.preventDefault();
												handleChangeEmail();
											}}
										>
											{email}
										</a>
									)}
								</span>
							</>
						) : (
							t(
								'auth.members_login_description',
								'Enter your email address and we’ll send you a code to access your prospectus.'
							)
						)}
					</p>

					<form
						ref={formRef}
						onSubmit={handleSubmit}
						aria-busy={isLoading}
					>
						{!showPasscode ? (
							<div className="field has-text-left">
								<input
									id="homepageEmailAddress"
									name="email"
									className="input"
									type="email"
									autoComplete="email"
									autoFocus
									required
									aria-label={t('auth.email_label', 'Email address')}
									value={email}
									disabled={isLoading}
									placeholder={t(
										'auth.email_example',
										AUTH_LOGIN_PLACEHOLDER_EMAIL
									)}
									onChange={(event) => setEmail(event.target.value)}
								/>
							</div>
						) : (
							<fieldset
								className={style.passcode}
								disabled={isLoading}
							>
								<legend className="is-sr-only">
									{t('auth.code_label', 'Six-digit login code')}
								</legend>

								<OneTimePassCode key={passcodeVersion} />
							</fieldset>
						)}

						{error && (
							<p className="has-text-danger mt-4" role="alert">
								{error}
							</p>
						)}

						{notice && (
							<p className="mt-4" role="status">
								{notice}
							</p>
						)}

						<div className={`${style.submit} mt-5`}>
							<Btn
								type="submit"
								customClass={['primary', 'buttonTextColours']}
								disabled={isLoading}
							>
								{isLoading
									? showPasscode
										? t('auth.logging_you_in', 'Logging you in...')
										: t('auth.sending_code', 'Sending code...')
									: showPasscode
										? t('home.view_prospectus', 'View Prospectus')
										: t('auth.send_login_code', 'Send login code')}
							</Btn>
						</div>

						{showPasscode && !codeActionsDisabled && (
							<p
								className={`has-text-align-center mt-3 ${style.requestCode}`}
							>
								<Btn
									type="button"
									onClick={() => requestCode(true)}
								>
									{t(
										'auth.request_new_code',
										'Request new code'
									)}
								</Btn>
							</p>
						)}
					</form>
				</div>
			)}
		</>
	);
}